package core.io

import common.EEnvironmentVariables
import core.extensions.getValue
import core.io.dto.ImageUploadDTOOut
import core.io.dto.MinifiedColourDTO
import core.io.dto.SceneDTOIn
import core.io.dto.TokenDTOIn
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.network.sockets.ConnectTimeoutException
import io.ktor.client.network.sockets.SocketTimeoutException
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import io.ktor.utils.io.core.*
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.serialization.json.Json
import module.watchlist.ColourListObject
import kotlin.coroutines.coroutineContext

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 23.09.22
 * Author : mariorosshirt
 */
class CustomHttpClient {

    // ----------------------------------------------------------------------------
    // Public properties
    // ----------------------------------------------------------------------------
    val mJsonClient: HttpClient
        get(){
        return HttpClient(Js) {
            install(HttpTimeout) {
                connectTimeoutMillis = 30_000
                requestTimeoutMillis = 30_000
                socketTimeoutMillis = 30_000
            }
            install(ContentNegotiation) {
                json(Json {
                    prettyPrint = true
                })
            }
        }
    }

    suspend fun uploadImage(base64String: String, application: Int): CustomHttpResponse<TokenDTOIn> {
        // somehow exceptions related to missing internet connection are not caught. Instead, they have been caught
        // in the CoroutineExceptionHandlerImpl.kt:21 without any change to catch it...
        if (!window.navigator.onLine) {
            return CustomHttpResponse.Error.NoInternet()
        }

        return try {
            val data: TokenDTOIn = mJsonClient.use {
                it.post("https://${EEnvironmentVariables.VEEUZE_HOST.getValue()}/api/token") {
                    contentType(ContentType.Application.Json)
                    setBody(ImageUploadDTOOut(base64String, application))
                }.body()
            }

            CustomHttpResponse.Success(data)
        } catch (exception: Exception) {
            when {
                exception is ConnectTimeoutException -> CustomHttpResponse.Error.Timeout()
                exception is SocketTimeoutException -> CustomHttpResponse.Error.Timeout()
                exception is HttpRequestTimeoutException -> CustomHttpResponse.Error.Timeout()
                exception.message?.contains("network", ignoreCase = true) == true -> CustomHttpResponse.Error.NoInternet()
                exception.message?.contains("host", ignoreCase = true) == true -> CustomHttpResponse.Error.NoInternet()
                else -> CustomHttpResponse.Error.Unknown()
            }
        }
    }

    suspend fun exportWatchList(colourList: List<ColourListObject>): HttpResponse {
        val response = mJsonClient.use {
            it.post("https://${EEnvironmentVariables.VEEUZE_HOST.getValue()}/api/exportWishList") {
                contentType(ContentType.Application.Json)
                setBody(colourList)
            }
        }
        return response
    }

    suspend fun exportProductList(colourList: List<ColourListObject>): HttpResponse {
        val response = mJsonClient.use {
            it.post("https://${EEnvironmentVariables.VEEUZE_HOST.getValue()}/api/exportProductList") {
                contentType(ContentType.Application.Json)
                setBody(colourList)
            }
        }
        return response
    }

    suspend fun getAdditionalImage(uniqueList: List<String>): List<MinifiedColourDTO> {
        val response: List<MinifiedColourDTO> = mJsonClient.use {
            it.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = EEnvironmentVariables.VEEUZE_HOST.getValue()
                    path("/api/additionalColours")
                    parameters.append("colourUniques", uniqueList.joinToString(separator = ","))
                }
                contentType(ContentType.Application.Json)
            }.body()
        }
        return response
    }

    suspend fun getGalleryImages(): List<SceneDTOIn> {
        val response: List<SceneDTOIn> = mJsonClient.use {
            it.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = EEnvironmentVariables.VEEUZE_HOST.getValue()
                    path("/api/scenes")
                }
                contentType(ContentType.Application.Json)
            }.body()
        }
        return response
    }

    // ----------------------------------------------------------------------------
    // Protected properties
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Private properties
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Internal properties
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Init
    // ----------------------------------------------------------------------------
    init {

    }

    // ----------------------------------------------------------------------------
    // Constructor
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // System/Overridden methods
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Public methods
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Protected methods
    // ----------------------------------------------------------------------------

    // ----------------------------------------------------------------------------
    // Private methods
    // ----------------------------------------------------------------------------
    /*
    private fun getFileFromResource(fileName: String): File? {
        val classLoader = javaClass.classLoader
        val resource: URL? = classLoader.getResource(fileName)
        return resource?.let { File(it.toURI()) }
    }

    private fun convertByteArray2Base64(byteArray: ByteArray): String {
        return "data:image/jpeg;base64,${Base64.getEncoder().encodeToString(byteArray)}"
    }
     */

    // ----------------------------------------------------------------------------
    // Interfaces/Listener
    // ----------------------------------------------------------------------------
}